import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { FXAAShader } from 'three/addons/shaders/FXAAShader.js';
import { GRADE, WATER } from '../config';
import type { SkyUniforms } from './scene';

// Full-frame colour grade (land + water): gentle filmic contrast, a saturation/lift/gain push, a
// touch of warm-highlight/cool-shadow split and a soft vignette, the underwater tint when the camera
// dips below the sea, and DEPTH-DRIVEN aerial perspective + a soft far-field blur (it samples the
// scene depth, so distance fades toward the sky/haze colour, desaturates, and blurs slightly instead
// of reading as a stark bright band). Pipeline order: RenderPass renders the scene LINEAR into the
// target (plus a depth texture), the grade runs in that linear space, then OutputPass tone maps
// (renderer.toneMapping stays ACESFilmic) + sRGB to screen. So we grade before OutputPass to avoid
// double tone-mapping, and any colours we inject (underwater, haze) are converted to linear first.

// Bake the underwater tint once (config is sRGB hex; the grade works in linear, so convert).
const UW = new THREE.Color(WATER.underwaterColor).convertSRGBToLinear();

const VERT = `
  varying vec2 vUv;
  void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

const FRAG = `
  uniform sampler2D tDiffuse;
  uniform sampler2D tDepth;        // scene depth, for aerial haze + DOF
  uniform float uNear;
  uniform float uFar;
  uniform float uFocusDist;        // camera->player distance: haze/DOF ramp BEYOND this (zoom-independent)
  uniform float uContrast;
  uniform float uSaturation;
  uniform float uLift;
  uniform float uGain;
  uniform float uWarmth;
  uniform float uVignette;
  uniform float uUnderwater;       // 0 above water .. 1 fully submerged
  uniform vec3 uUnderwaterColor;   // linear-space tint
  uniform float uNight;            // 0 day .. 1 night (darken + blue tint)
  uniform vec3 uSkyHorizon, uSkyZenith, uSunDir, uSunColor; // sky-dome colours (linear) to blend distance into
  uniform vec3 uCamPos;
  uniform mat4 uInvVP;             // inverse(proj*view): reconstruct each pixel's world view ray
  uniform float uHazeStart, uHazeEnd, uHazeStrength, uHazeDesat;
  uniform float uDofStart, uDofEnd, uDofRadius;
  uniform float uStormDim;   // local rain at the player 0..1 -> darken + blue haze (the ominous storm mood)
  uniform float uFlash;      // lightning flash 0..1 -> brief global white brighten
  uniform float uHurt;       // damage-taken flash 0..1 -> brief red vignette pulse (decays in Grade.tick)
  uniform float uGodRay;     // crepuscular-ray strength 0..1 (sun + scattered cloud, daytime)
  uniform vec2 uSunScreen;   // sun position in screen UV (god-ray origin)
  uniform vec2 resolution;
  uniform float uFog;        // volumetric height-fog strength 0..1 (0 = skipped)
  uniform vec3 uFogColor;    // linear mist colour (sky-horizon tinted, set per frame)
  uniform float uFogBottom, uFogTop; // world heights: full density at/below Bottom, zero at/above Top
  uniform float uFogDensity; // Beer-Lambert fill rate per metre of view distance
  varying vec2 vUv;

  // World distance (metres) from the camera at a screen uv. The renderer uses a LOGARITHMIC depth
  // buffer, so invert that (not the standard perspective depth): d = log2(1 - viewZ)/log2(far+1).
  float sceneDist(vec2 uv){
    float d = texture2D(tDepth, uv).x;
    return exp2(d * log2(uFar + 1.0)) - 1.0;
  }

  // The sky-dome colour along a world direction (matches scene.ts: horizon->zenith gradient + a soft low
  // sun halo). Distant terrain blends into THIS, so the horizon is seamless instead of a flat white band.
  vec3 skyAt(vec3 dir){
    float y = clamp(dir.y, 0.0, 1.0);
    float g = pow(smoothstep(0.0, 1.0, y), 0.8);
    vec3 col = mix(uSkyHorizon, uSkyZenith, g);
    float sd = max(dot(dir, normalize(uSunDir)), 0.0);
    col += uSunColor * pow(sd, 6.0) * 0.18 * smoothstep(0.0, 0.08, dir.y);
    return col;
  }

  void main(){
    float dist = sceneDist(vUv);
    // Ramp haze + DOF on distance BEYOND the player (the focus), not absolute camera distance, so the
    // player's surroundings always stay sharp/clear and zooming out never blurs the foreground.
    float beyond = max(dist - uFocusDist, 0.0);

    // Depth of field: a soft disk blur whose radius grows with distance. Subtle (a few pixels), so the
    // far field reads photographic without smearing. 6-tap ring + centre; skipped when in focus.
    float blur = smoothstep(uDofStart, uDofEnd, beyond) * uDofRadius;
    vec3 c;
    if (blur < 0.4) {
      c = texture2D(tDiffuse, vUv).rgb;
    } else {
      vec2 px = blur / resolution;
      c = texture2D(tDiffuse, vUv).rgb;
      c += texture2D(tDiffuse, vUv + px * vec2( 0.900,  0.000)).rgb;
      c += texture2D(tDiffuse, vUv + px * vec2( 0.450,  0.779)).rgb;
      c += texture2D(tDiffuse, vUv + px * vec2(-0.450,  0.779)).rgb;
      c += texture2D(tDiffuse, vUv + px * vec2(-0.900,  0.000)).rgb;
      c += texture2D(tDiffuse, vUv + px * vec2(-0.450, -0.779)).rgb;
      c += texture2D(tDiffuse, vUv + px * vec2( 0.450, -0.779)).rgb;
      c /= 7.0;
    }

    // Aerial perspective: distance blends toward the ACTUAL sky colour along this pixel's view ray (the
    // same dome gradient + sun glow), so distant land melts into the horizon instead of a flat haze band.
    float haze = smoothstep(uHazeStart, uHazeEnd, beyond);
    vec4 wp = uInvVP * vec4(vUv * 2.0 - 1.0, 1.0, 1.0);
    vec3 viewDir = normalize(wp.xyz / wp.w - uCamPos);
    vec3 skyCol = skyAt(viewDir);
    float hl = dot(c, vec3(0.2126, 0.7152, 0.0722));
    c = mix(c, vec3(hl), haze * uHazeDesat);
    // At night the aerial haze switches OFF almost entirely: with near-black ground, blending distant terrain
    // toward the (relatively brighter) sky lifted it into a grey/white band. Faded out hard, distant land stays
    // a dark silhouette against the sky, the way a real night horizon reads (owner: night distance = BLACK).
    c = mix(c, skyCol, haze * uHazeStrength * (1.0 - 0.99 * clamp(uNight, 0.0, 1.0)));

    // VOLUMETRIC HEIGHT FOG: a low, ground-hugging mist that pools near/below the waterline + in low ground,
    // accumulating with distance so it reads volumetric, and thickening where rain falls (uFog already folds in
    // the local-rain boost). Reuses viewDir + dist (no new depth taps); skipped when off. Reconstruct the pixel's
    // world height along the view ray, take the vertical density there (full at/below uFogBottom, 0 by uFogTop),
    // and Beer-Lambert accumulate through the layer. The path is capped so the far sky can't whiteout the frame.
    if (uFog > 0.001) {
      float wy = uCamPos.y + viewDir.y * dist;                       // this pixel's world height (m)
      float heightT = clamp((uFogTop - wy) / max(uFogTop - uFogBottom, 0.001), 0.0, 1.0);
      heightT = heightT * heightT;                                   // bias the falloff so it hugs the low band
      float md = min(dist, 1200.0);                                  // cap the path -> a near-ground band, not a whiteout
      float fogAmt = (1.0 - exp(-md * uFogDensity * heightT)) * uFog;
      // Kill the ground mist at night: a pale pooled band over distant low ground is exactly the "fog that
      // shouldn't be there after dark" -- at deep night distance must fall to black, not a lit mist layer.
      fogAmt *= (1.0 - 0.99 * clamp(uNight, 0.0, 1.0));
      c = mix(c, uFogColor, clamp(fogAmt, 0.0, 0.92));
    }

    // STORM: when rain is overhead, dim the world + pull it toward an ominous cool blue haze.
    if (uStormDim > 0.001) {
      float sd = clamp(uStormDim, 0.0, 1.0);
      float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
      c = mix(c, vec3(l), 0.22 * sd);                       // desaturate
      c = mix(c, vec3(0.05, 0.08, 0.14), 0.20 * sd);        // cool blue haze
      c *= mix(1.0, 0.72, sd);                              // darken (eased so the storm isn't crushing)
    }

    // GOD RAYS (crepuscular shafts): march from this pixel toward the sun's screen position, accumulating the
    // BRIGHT (sky/sun) samples along the way -- where dark occluders (trees, hills, cloud edges) break the bright
    // sky, the gaps read as light shafts radiating from the sun. Added in linear light so it tone-maps naturally.
    // Gated to daytime + scattered cloud (uGodRay), so it's free on clear skies, at night, and in heavy storms.
    if (uGodRay > 0.001) {
      vec2 stepv = (uSunScreen - vUv) / 28.0;
      vec2 suv = vUv;
      float illum = 0.0, decay = 1.0;
      for (int i = 0; i < 28; i++) {
        suv += stepv;
        vec3 s = texture2D(tDiffuse, clamp(suv, 0.0, 1.0)).rgb;
        illum += smoothstep(0.30, 0.9, dot(s, vec3(0.2126, 0.7152, 0.0722))) * decay;
        decay *= 0.95;
      }
      illum /= 28.0;
      c += uSunColor * illum * uGodRay * 1.7;
    }

    // Lift (raise blacks) then gain (scale), the cheap two-knob exposure trim.
    c = (c + uLift) * uGain;

    // Filmic contrast pivoted around mid grey, not 0, so it does not just darken.
    c = (c - 0.5) * uContrast + 0.5;

    // Saturation: pull toward / push from luma (Rec.709 weights).
    float luma = dot(c, vec3(0.2126, 0.7152, 0.0722));
    c = mix(vec3(luma), c, uSaturation);

    // Warm/cool split: warm the highlights, cool the shadows, scaled by warmth. Subtle by design.
    float t = luma * 2.0 - 1.0;          // -1 shadow .. +1 highlight
    c.r += uWarmth * t;
    c.b -= uWarmth * t;

    // Radial vignette: darken toward the corners (aspect-corrected so it stays round).
    vec2 d = (vUv - 0.5) * vec2(resolution.x / resolution.y, 1.0);
    float vig = smoothstep(0.85, 0.25, length(d) * 1.35);
    c *= mix(1.0, vig, uVignette);

    // Damage flash: a RED vignette pulse (stronger at the edges, center stays readable) that fades over
    // ~DAMAGE_FX.flashDecayPerSec (main.ts ticks uHurt down each frame). Distinct from uFlash (lightning's
    // white brighten) so the two never fight -- this darkens/reddens rather than brightens.
    if (uHurt > 0.0) {
      float hurtVig = smoothstep(0.15, 0.95, length(d) * 1.1);
      c = mix(c, vec3(0.5, 0.02, 0.02), hurtVig * uHurt * 0.65);
      c *= mix(1.0, 0.82, uHurt); // slight overall darken so a big hit reads as a jolt, not just a red tint
    }

    // Night: darken the whole frame and tint it toward moonlit blue, so dusk -> night reads even on
    // self-lit things like grass. Driven by the day-night cycle (1 - dayAmount).
    if (uNight > 0.0) {
      float nn = clamp(uNight, 0.0, 1.0);
      float nl = dot(c, vec3(0.2126, 0.7152, 0.0722));
      c = mix(c, vec3(nl), 0.22 * nn);                  // slight desaturate
      // Real darkness comes from near-zero ambient (daynight.ts), so keep the post grade light: a faint
      // blue tint and a gentle darken. Heavier values here would crush torch-lit pixels and lift true
      // blacks (which screen gamma could fight). Torch + lights stay bright; unlit stays black.
      c = mix(c, vec3(0.02, 0.035, 0.07), 0.14 * nn);   // faint night blue (low, so blacks stay black)
      c *= mix(1.0, 0.78, nn);                          // darken toward a Rust-dark night (torch pixels stay lit)

    }

    // Underwater: a cheap depth tint. Lerp toward the water colour, knock down brightness and
    // saturation as you sink. No ripples/caustics on purpose, this stays a flat per-pixel grade.
    if (uUnderwater > 0.0) {
      float u = clamp(uUnderwater, 0.0, 1.0);
      float dl = dot(c, vec3(0.2126, 0.7152, 0.0722));
      c = mix(c, vec3(dl), 0.35 * u);                 // desaturate with depth
      c = mix(c, uUnderwaterColor, 0.55 * u);         // tint toward the water colour
      c *= mix(1.0, 0.65, u);                          // darken with depth (less light gets down)
    }

    c += vec3(0.85, 0.92, 1.0) * uFlash; // lightning flash: brief global whiten
    gl_FragColor = vec4(max(c, 0.0), 1.0);
  }`;

export class Grade {
  private readonly composer: EffectComposer;
  private readonly gradePass: ShaderPass;
  private readonly fxaaPass: ShaderPass;
  private readonly camera: THREE.Camera;
  private readonly depthTexture: THREE.DepthTexture;
  private focus: THREE.Vector3 | null = null; // the player focus point; haze/DOF ramp beyond it
  private readonly invVP = new THREE.Matrix4(); // scratch: inverse(proj*view) for the view-ray haze
  private godRayStrength = 0;                   // god-ray strength from main; zeroed when the sun is behind camera
  private readonly sunClip = new THREE.Vector4(); // scratch: project the sun to screen for the god-ray origin

  constructor(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera) {
    this.camera = camera;
    this.composer = new EffectComposer(renderer);

    // Scene depth: attach one depth texture to BOTH composer targets so whichever one the RenderPass
    // writes into fills it, and the grade pass can sample it. The grade pass must not write depth (set
    // below) or it would feed back into the same texture it reads.
    this.depthTexture = new THREE.DepthTexture(1, 1);
    this.depthTexture.type = THREE.UnsignedIntType;
    this.composer.renderTarget1.depthTexture = this.depthTexture;
    this.composer.renderTarget2.depthTexture = this.depthTexture;
    // Hardware MSAA on the scene render so geometry EDGES resolve crisply (the renderer's own antialias:true
    // is dead once we render through the composer). three.js resolves the multisampled colour AND the depth
    // texture on read, so the grade pass's depth-based haze/DOF still get clean depth. FXAA stays on after
    // OutputPass to also clean the alpha-tested foliage/billboard edges that MSAA alone can't.
    this.composer.renderTarget1.samples = 4;
    this.composer.renderTarget2.samples = 4;

    this.gradePass = new ShaderPass({
      uniforms: {
        tDiffuse: { value: null },
        tDepth: { value: this.depthTexture },
        uNear: { value: 0.1 },
        uFar: { value: 1000 },
        uFocusDist: { value: 120 },
        uContrast: { value: GRADE.contrast },
        uSaturation: { value: GRADE.saturation },
        uLift: { value: GRADE.lift },
        uGain: { value: GRADE.gain },
        uWarmth: { value: GRADE.warmth },
        uVignette: { value: GRADE.vignette },
        uUnderwater: { value: 0 },
        uUnderwaterColor: { value: new THREE.Vector3(UW.r, UW.g, UW.b) },
        uNight: { value: 0 },
        // Sky-dome colours (shared from world.sky via setSky so the day-night cycle drives them) + the
        // camera reconstruction uniforms for the per-pixel view ray. Defaults until setSky runs.
        uSkyHorizon: { value: new THREE.Color(0x9ec2e0).convertSRGBToLinear() },
        uSkyZenith: { value: new THREE.Color(0x3f78d8).convertSRGBToLinear() },
        uSunDir: { value: new THREE.Vector3(0, 1, 0) },
        uSunColor: { value: new THREE.Color(0xfff2da).convertSRGBToLinear() },
        uCamPos: { value: new THREE.Vector3() },
        uInvVP: { value: new THREE.Matrix4() },
        uHazeStart: { value: GRADE.hazeStart },
        uHazeEnd: { value: GRADE.hazeEnd },
        uHazeStrength: { value: GRADE.hazeStrength },
        uHazeDesat: { value: GRADE.hazeDesat },
        uDofStart: { value: GRADE.dofStart },
        uDofEnd: { value: GRADE.dofEnd },
        uDofRadius: { value: GRADE.dofRadius },
        uStormDim: { value: 0 },
        uFlash: { value: 0 },
        uHurt: { value: 0 },
        uGodRay: { value: 0 },
        uSunScreen: { value: new THREE.Vector2(0.5, 0.5) },
        resolution: { value: new THREE.Vector2(1, 1) },
        // Volumetric height fog (live-read from GRADE in render()).
        uFog: { value: 0 },               // effective strength this frame (base + rain), 0 = skipped
        uFogColor: { value: new THREE.Vector3(0.7, 0.78, 0.86) }, // linear mist colour (set from sky each frame)
        uFogBottom: { value: -16 },        // world height of full density
        uFogTop: { value: 9 },             // world height where density reaches 0
        uFogDensity: { value: 0.011 },     // Beer-Lambert fill per metre
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
    });
    // The grade pass reads the depth texture, so it must NOT write depth into it (avoids a feedback
    // hazard, since that texture is also attached to the target it renders into).
    this.gradePass.material.depthWrite = false;
    this.gradePass.material.depthTest = false;

    // Edge anti-aliasing on the COMPOSITED frame. The scene renders through this composer, so the
    // renderer's own MSAA (antialias:true) never touches the final image - geometry edges would be
    // jagged everywhere. FXAA is a cheap full-screen pass that cleans them (slight softening).
    this.fxaaPass = new ShaderPass(FXAAShader);

    // RenderPass (scene -> linear target + depth), grade (linear), OutputPass (tone map + sRGB), then
    // FXAA last so it anti-aliases the final display-referred (sRGB) image, where its luma edge detection
    // works best.
    this.composer.addPass(new RenderPass(scene, camera));
    this.composer.addPass(this.gradePass);
    this.composer.addPass(new OutputPass());
    this.composer.addPass(this.fxaaPass);
  }

  setSize(width: number, height: number): void {
    const pr = this.composer.renderer.getPixelRatio();
    this.composer.setPixelRatio(pr);
    this.composer.setSize(width, height); // resizes the render targets + the shared depth texture
    this.gradePass.uniforms.resolution.value.set(width, height);
    // FXAA works on the composer's actual buffer (width*pixelRatio); its resolution uniform is 1/texel.
    this.fxaaPass.material.uniforms.resolution.value.set(1 / (width * pr), 1 / (height * pr));
  }

  // 0 = above water, 1 = fully submerged. Drives the underwater tint/darkening.
  setUnderwater(t: number): void {
    this.gradePass.uniforms.uUnderwater.value = t;
  }

  // 0 = full day, 1 = night. Darkens + blue-tints the whole frame (driven by the day-night cycle).
  setNight(t: number): void {
    this.gradePass.uniforms.uNight.value = t;
  }

  // Local rain at the player 0..1 -> the storm dim/blue-haze post.
  setStormDim(t: number): void {
    this.gradePass.uniforms.uStormDim.value = t;
  }

  // Lightning flash 0..1 -> brief global white brighten.
  setFlash(t: number): void {
    this.gradePass.uniforms.uFlash.value = t;
  }

  // Damage-taken flash 0..1 -> the red vignette pulse. main.ts owns the decay (DAMAGE_FX.flashDecayPerSec)
  // and calls this every frame with the current value, same idiom as setFlash.
  setHurt(t: number): void {
    this.gradePass.uniforms.uHurt.value = t;
  }

  // Crepuscular (god-ray) strength 0..1 -> sun shafts through scattered cloud. Applied only when the sun is in
  // front of the camera (render() gates it); free otherwise.
  setGodRay(t: number): void {
    this.godRayStrength = t;
  }

  // The player focus point (a live Vector3 ref). Haze/DOF ramp at distances BEYOND the camera->focus
  // distance, so the surroundings stay sharp/clear and zooming never blurs the foreground.
  setFocus(focus: THREE.Vector3): void {
    this.focus = focus;
  }

  // Share the live sky-dome colour uniforms (from world.sky) so the distance haze blends into the EXACT
  // day-night sky colour behind each pixel - no more flat white horizon band. Call once after construction.
  setSky(sky: SkyUniforms): void {
    const u = this.gradePass.uniforms;
    u.uSkyHorizon = sky.uHorizon;
    u.uSkyZenith = sky.uZenith;
    u.uSunDir = sky.uSunDir;
    u.uSunColor = sky.uSunColor;
  }

  // Call instead of renderer.render(scene, camera). Allocation-free per frame. Pulls the live GRADE
  // values into the uniforms first, so the admin panel's grade edits apply with no recompile.
  render(): void {
    const u = this.gradePass.uniforms;
    u.uContrast.value = GRADE.contrast;
    u.uSaturation.value = GRADE.saturation;
    u.uLift.value = GRADE.lift;
    u.uGain.value = GRADE.gain;
    u.uWarmth.value = GRADE.warmth;
    u.uVignette.value = GRADE.vignette;
    u.uHazeStart.value = GRADE.hazeStart;
    u.uHazeEnd.value = GRADE.hazeEnd;
    u.uHazeStrength.value = GRADE.hazeStrength;
    u.uHazeDesat.value = GRADE.hazeDesat;
    u.uDofStart.value = GRADE.dofStart;
    u.uDofEnd.value = GRADE.dofEnd;
    u.uDofRadius.value = GRADE.dofRadius;
    // Volumetric fog: a small always-on shore mist (fogBase) plus a rain-driven boost. uStormDim is the LOCAL
    // rain at the player (set by main via setStormDim), so the fog thickens exactly where the storm reads.
    {
      const g = GRADE as unknown as Record<string, number>;
      const rain = (u.uStormDim as { value: number }).value;
      u.uFog.value = Math.min(g.fog * (g.fogBase + g.fogRainBoost * rain), 1);
      u.uFogBottom.value = g.fogBottom;
      u.uFogTop.value = g.fogTop;
      u.uFogDensity.value = g.fogDensity;
      // Mist colour = the LIVE sky-horizon hue (tracks day-night + the storm sky-mute) lifted toward
      // white BY DAY ONLY: the lift dies with the sun, because lifting at night is exactly what painted
      // a pale grey wall behind the dark silhouettes (owner: after dark, distance falls to BLACK).
      const sh = (u.uSkyHorizon as { value: THREE.Color }).value;
      const sdy = (u.uSunDir as { value: THREE.Vector3 }).value.y;
      const k = g.fogLighten * Math.min(1, Math.max(0, sdy * 3 + 0.25));
      (u.uFogColor as { value: THREE.Vector3 }).value.set(sh.r + (1 - sh.r) * k, sh.g + (1 - sh.g) * k, sh.b + (1 - sh.b) * k);
    }
    // Track the live camera near/far + focus distance, and the inverse view-projection so the haze can
    // reconstruct each pixel's world view ray and blend distance into the matching sky colour.
    const cam = this.camera as THREE.PerspectiveCamera;
    if (cam.isPerspectiveCamera) { u.uNear.value = cam.near; u.uFar.value = cam.far; }
    if (this.focus) u.uFocusDist.value = this.camera.position.distanceTo(this.focus);
    this.camera.updateMatrixWorld();
    this.invVP.copy(this.camera.matrixWorld).invert();      // view = inverse(world)
    this.invVP.premultiply(this.camera.projectionMatrix);   // proj * view (forward VP, used next for the sun)
    u.uCamPos.value.setFromMatrixPosition(this.camera.matrixWorld);
    // God-ray origin: project a point far along the sun direction to screen UV. If it's behind the camera
    // (w <= 0) the shafts make no sense, so zero the effect this frame.
    if (this.godRayStrength > 0.001) {
      const cp = u.uCamPos.value, sd = u.uSunDir.value;
      this.sunClip.set(cp.x + sd.x * 5000, cp.y + sd.y * 5000, cp.z + sd.z * 5000, 1).applyMatrix4(this.invVP);
      if (this.sunClip.w > 0) {
        u.uSunScreen.value.set(this.sunClip.x / this.sunClip.w * 0.5 + 0.5, this.sunClip.y / this.sunClip.w * 0.5 + 0.5);
        u.uGodRay.value = this.godRayStrength;
      } else { u.uGodRay.value = 0; }
    } else { u.uGodRay.value = 0; }
    this.invVP.invert();                                    // inverse(proj * view) for the view-ray haze
    u.uInvVP.value.copy(this.invVP);
    this.composer.render();
  }
}
